import React, { useState, useEffect } from "react";
import { Table } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useSearchParams, useNavigate } from "react-router-dom";
import { DataTableProps } from "./types";
import { TableHeader } from "./table-header";
import { TableBody } from "./table-body";
import { Pagination } from "./pagination";
import { normalizeText } from "@/utils/textFormatter";

export const DataTable = <T,>({
  columns,
  data: initialData,
  pageSize: initialPageSize = 10,
  className,
  onEdit,
  minWidth = "1800px",
}: DataTableProps<T>) => {
  const [searchParams, _setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof T | null;
    direction: "asc" | "desc" | null;
  }>({
    key: null,
    direction: null,
  });
  const [currentPage, setCurrentPage] = useState(1);

  // Initialize from URL params
  useEffect(() => {
    const page = searchParams.get("page");
    const size = searchParams.get("size");
    if (page) setCurrentPage(Number(page));
    if (size) setPageSize(Number(size));
  }, []);

  // Update URL when pagination changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("page", currentPage.toString());
    params.set("size", pageSize.toString());
    navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });
  }, [currentPage, pageSize]);

  // Sorting logic
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return initialData;

    return [...initialData].sort((a, b) => {
      let aValue = a[sortConfig.key!];
      let bValue = b[sortConfig.key!];

      // Special handling for accountState to sort by isRegistered boolean
      if (sortConfig.key === "accountState") {
        aValue = (a as any).accountState?.isRegistered ?? false;
        bValue = (b as any).accountState?.isRegistered ?? false;
      }

      if (aValue === bValue) return 0;

      // Handle boolean values
      if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        if (sortConfig.direction === "asc") {
          return aValue === bValue ? 0 : aValue ? 1 : -1;
        } else {
          return aValue === bValue ? 0 : aValue ? -1 : 1;
        }
      }

      // Handle string values with normalization for special characters
      if (typeof aValue === "string" && typeof bValue === "string") {
        const normalizedA = normalizeText(aValue);
        const normalizedB = normalizeText(bValue);

        if (sortConfig.direction === "asc") {
          return normalizedA.localeCompare(normalizedB);
        } else {
          return normalizedB.localeCompare(normalizedA);
        }
      }

      // Handle other values (numbers, etc.)
      if (sortConfig.direction === "asc") {
        return aValue < bValue ? -1 : 1;
      } else {
        return aValue > bValue ? -1 : 1;
      }
    });
  }, [initialData, sortConfig]);

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const paginatedData = sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Ensure current page is valid when data length or page size changes
  React.useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(sortedData.length / pageSize));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [sortedData.length, pageSize, currentPage]);

  const handleSort = (columnKey: keyof T) => {
    setSortConfig((current) => ({
      key: columnKey,
      direction:
        current.key === columnKey && current.direction === "asc"
          ? "desc"
          : current.key === columnKey && current.direction === "desc"
          ? null
          : "asc",
    }));
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  return (
    <div className={cn("rounded-xl bg-white overflow-hidden shadow-sm w-full", className)}>
      <div className="data-table-container">
        <Table className="data-table" style={{ minWidth: minWidth }}>
          <TableHeader columns={columns} sortConfig={sortConfig} onSort={handleSort} />
          <TableBody columns={columns} data={paginatedData} onEdit={onEdit} />
        </Table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalItems={sortedData.length}
        onPageChange={setCurrentPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};
